import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import "react-day-picker/dist/style.css";
import { format, differenceInCalendarDays, addDays, parseISO } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, Users, BedDouble, Loader2 } from "lucide-react";
import { StayHeader } from "@/components/stays/StayHeader";
import { PhotoGallery } from "@/components/stays/PhotoGallery";
import { AmenityChips } from "@/components/stays/AmenityChips";
import { DetailSkeleton } from "@/components/stays/Skeletons";
import { RatingBadge } from "@/components/stays/booking/RatingBadge";
import { BookingFields } from "@/components/stays/booking/BookingFields";
import { InlineCalendar } from "@/components/stays/booking/InlineCalendar";
import { GuestDetailsForm } from "@/components/stays/booking/GuestDetailsForm";
import { CouponField } from "@/components/stays/booking/CouponField";
import { PriceBreakdown } from "@/components/stays/booking/PriceBreakdown";
import { TrustBadges } from "@/components/stays/booking/TrustBadges";
import { ReserveSheet } from "@/components/stays/booking/ReserveSheet";
import { MobileCheckoutFlow } from "@/components/stays/booking/MobileCheckoutFlow";
import { PropertyRules } from "@/components/stays/PropertyRules";
import Footer from "@/components/Footer";
import { getRooms, getProperty, getAvailability, createBooking, validateCoupon, friendlyError, bookingError, fmtINR, fmtAmount, getRules, getFeeQuote, istToday, waitForPaymentLink, validPhoneIntl, validEmailAddr } from "@/lib/stayflowApi";
import { roomMedia, guestLimits } from "@/lib/propertyRules";
import { getProfile, saveProfile } from "@/lib/profile";
import { emptyGst, gstClientError, gstPayload, gstBackendError, invoiceeLine, billingConflict } from "@/lib/gstBilling";
import { setMeta, SITE_URL } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { apiLinkBooking } from "@/lib/userAuth";
import { AuthModal } from "@/components/auth/AuthModal";
import { LogIn, ShieldCheck, Check } from "lucide-react";

const iso = (d) => format(d, "yyyy-MM-dd");

const RoomBooking = () => {
  const { propertyId, roomId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  const roomsQ = useQuery({ queryKey: ["rooms", propertyId], queryFn: () => getRooms(propertyId), staleTime: 5 * 60 * 1000, retry: 1 });
  const propQ = useQuery({ queryKey: ["property", propertyId], queryFn: () => getProperty(propertyId), staleTime: 5 * 60 * 1000, retry: 1 });

  const room = useMemo(() => (roomsQ.data || []).find((r) => String(r.id) === String(roomId)), [roomsQ.data, roomId]);

  const today = useMemo(() => istToday(), []);
  const windowEnd = useMemo(() => addDays(today, 180), [today]);

  // Closed dates always come live from the server — never served from cache.
  const availQ = useQuery({
    queryKey: ["availability", roomId],
    queryFn: () => getAvailability(roomId, iso(today), iso(windowEnd)),
    enabled: !!roomId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const rulesQ = useQuery({
    queryKey: ["rules", propertyId],
    queryFn: () => getRules(propertyId),
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const rules = rulesQ.data?.recorded === false ? null : rulesQ.data || null;

  const busyDates = useMemo(
    () => (availQ.data?.busy_days || []).map((s) => { const d = parseISO(s); d.setHours(0, 0, 0, 0); return d; }),
    [availQ.data]
  );

  const [range, setRange] = useState(() => {
    const ci = sp.get("checkIn"), co = sp.get("checkOut");
    try {
      if (ci && co) {
        const from = parseISO(ci), to = parseISO(co);
        if (from >= istToday() && to > from) return { from, to };
      }
    }
    catch (e) { console.warn("Ignoring invalid checkIn/checkOut URL params:", e); }
    return undefined;
  });
  const [adults, setAdults] = useState(() => Math.max(1, Number(sp.get("adults")) || Number(sp.get("guests")) || 2));
  const [children, setChildren] = useState(() => Math.max(0, Number(sp.get("children")) || 0));
  const [rooms, setRooms] = useState(() => Math.max(1, Number(sp.get("rooms")) || 1));
  const guests = adults + children; // total, kept for capacity + backward-compat
  const [form, setForm] = useState(() => ({ guest_name: "", guest_phone: "", guest_email: "", special_requests: "", ...getProfile() }));
  const [gst, setGst] = useState(emptyGst);
  const [gstError, setGstError] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);

  useEffect(() => {
    if (room) setMeta({ title: `Book ${room.name} — StayFlow`, description: room.description || `Book ${room.name} with secure payment.`, image: room.cover_image || undefined, url: `${SITE_URL}/stays/${propertyId}/${roomId}` });
  }, [room, propertyId, roomId]);

  // Autofill guest details from the signed-in account (only into empty fields).
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      guest_name: f.guest_name || user.name || "",
      guest_email: f.guest_email || user.email || "",
      guest_phone: f.guest_phone || user.phone || "",
    }));
  }, [user]);

  const { maxAdults, maxChildren, childRuleText } = useMemo(() => guestLimits(room), [room]);
  const childrenAllowed = rules?.children_allowed?.value !== false && maxChildren > 0;
  useEffect(() => {
    if (!room) return; // limits are unknown until the room loads — don't clamp yet
    if (!childrenAllowed && children > 0) setChildren(0);
    if (adults > maxAdults) setAdults(maxAdults);
    if (children > maxChildren) setChildren(maxChildren);
  }, [room, maxAdults, adults, children, maxChildren, childrenAllowed]);

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;
  const priceInclGst = room?.price_per_night_incl_gst;

  // reset coupon when dates change
  useEffect(() => { setCoupon(null); }, [nights]);

  // The server prices the stay: GST, fees, the exact amount Razorpay charges,
  // plus the cancellation terms for the chosen check-in.
  const quoteQ = useQuery({
    queryKey: ["feeQuote", roomId, range?.from ? iso(range.from) : null, range?.to ? iso(range.to) : null, adults, children],
    queryFn: () => getFeeQuote({ room_id: roomId, check_in: iso(range.from), check_out: iso(range.to), adults, children }),
    enabled: !!roomId && nights > 0,
    staleTime: 60 * 1000,
    retry: 1,
  });
  const quote = quoteQ.data || null;
  const quoteError = quoteQ.isError ? bookingError(quoteQ.error) : "";
  const quoteTerms = quote?.cancellation_terms || null;
  const total = quote?.amount_payable ?? null;
  const subtotal = quote?.room_total ?? null;

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!subtotal) { toast.error("Pick your dates first."); return; }
    setCouponLoading(true);
    try {
      const c = await validateCoupon(couponCode.trim(), subtotal);
      setCoupon(c);
      toast.success(`Coupon applied — you saved ${fmtINR(c.discount) || c.discount}`);
    } catch (err) {
      setCoupon(null);
      toast.error(friendlyError(err, "coupon"));
    } finally {
      setCouponLoading(false);
    }
  };

  const couponProps = {
    couponCode, setCouponCode, coupon,
    onApply: applyCoupon,
    onClear: () => { setCoupon(null); setCouponCode(""); },
    loading: couponLoading,
  };

  const validate = () => {
    if (!range?.from || !range?.to) return "Please select your check-in and check-out dates.";
    if (nights < 1) return "Your stay must be at least 1 night.";
    if (!form.guest_name.trim()) return "Please enter your name.";
    if (!validPhoneIntl(form.guest_phone)) return "Enter a WhatsApp number with its country code, e.g. +91 98765 43210.";
    if (!validEmailAddr(form.guest_email)) return "Please enter a valid email address.";
    return null;
  };

  const submit = async () => {
    const errMsg = validate();
    if (errMsg) { toast.error(errMsg); return false; }
    const gErr = gstClientError(gst);
    if (gErr) { setGstError(gErr); toast.error(gErr); return false; }
    if (submitting) return false;
    setSubmitting(true);
    setGstError("");
    try {
      const body = {
        room_id: roomId,
        guest_name: form.guest_name.trim(),
        guest_phone: form.guest_phone.trim().replace(/\s/g, ""),
        guest_email: form.guest_email.trim(),
        check_in: iso(range.from),
        check_out: iso(range.to),
        guests,
        adults,
        children,
        rooms,
        source: "website",
        special_requests: form.special_requests.trim(),
        ...gstPayload(gst),
      };
      const booking = await createBooking(body);
      try {
        localStorage.setItem("sf_last_booking", JSON.stringify({ id: booking.id, phone: body.guest_phone, email: body.guest_email, property_id: propertyId, check_in: body.check_in, check_out: body.check_out, bill_to_company: body.bill_to_company, bill_to_gstin: body.bill_to_gstin, payment_message: booking.payment_message || "" }));
        saveProfile({ guest_name: body.guest_name, guest_email: body.guest_email, guest_phone: body.guest_phone });
      }
      catch (e) { console.warn("Couldn't remember booking locally (storage unavailable):", e); }
      // Link this booking to the signed-in account so it appears in "My bookings".
      if (user) {
        try {
          await apiLinkBooking({
            booking_id: booking.id,
            property_id: propertyId,
            property_name: propQ.data?.name,
            room_name: room?.name,
            check_in: body.check_in,
            check_out: body.check_out,
            amount: booking.amount_payable ?? total,
            status: booking.status || "pending",
            cover_image: room?.cover_image,
          });
        } catch (e) { console.warn("Couldn't link booking to account:", e); }
      }
      // Link may arrive a few seconds after the booking — keep "Setting up secure payment…" and re-check.
      const link = booking.payment_link_url || (await waitForPaymentLink(booking.id));
      if (link) {
        const invoicee = invoiceeLine(body.bill_to_company, body.bill_to_gstin);
        toast.success("Redirecting to secure payment…", invoicee ? { description: invoicee } : undefined);
        window.location.href = link;
        return true;
      }
      // Still no link — the status page shows the server's message with Try again / Resume payment.
      setSubmitting(false);
      navigate(`/booking/${booking.id}/status`);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 && gst.enabled) {
        const msg = gstBackendError(err);
        setGstError(msg);
        toast.error(msg);
        setSubmitting(false);
        return false;
      }
      if (status === 409 && billingConflict(err)) {
        toast.error("Billing details can't be changed after payment");
        setSubmitting(false);
        return false;
      }
      toast.error(bookingError(err));
      setSubmitting(false);
      return false;
    }
  };

  const goConfirm = () => {
    const errMsg = validate();
    // Allow proceeding even without full guest details — checkout collects them.
    if (!range?.from || !range?.to || nights < 1) { toast.error("Please select your check-in and check-out dates."); return; }
    const params = new URLSearchParams();
    params.set("propertyId", propertyId);
    params.set("roomId", roomId);
    params.set("checkIn", iso(range.from));
    params.set("checkOut", iso(range.to));
    params.set("adults", String(adults));
    params.set("children", String(children));
    navigate(`/book/confirm?${params.toString()}`);
    void errMsg;
  };

  // Only this room's own media — never a stand-in from the property.
  const media = useMemo(() => roomMedia(room), [room]);

  const loading = roomsQ.isLoading || propQ.isLoading;

  return (
    <div className="bg-paper min-h-screen pb-24 lg:pb-0" data-testid="room-booking-page">
      <StayHeader />
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <Link to={`/stays/${propertyId}`} className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand transition-colors mb-6" data-testid="room-back">
          <ArrowLeft className="w-4 h-4" /> Back to rooms
        </Link>

        {/* Sign-in nudge — booking stays optional (no gate), but signing in saves it */}
        {!loading && room && (
          user ? (
            <div className="mb-6 flex items-center gap-3 rounded-2xl border border-black/8 bg-white px-4 py-3.5 shadow-[0_1px_0_rgba(0,0,0,0.02)]" data-testid="booking-signed-in">
              <span className="shrink-0 w-10 h-10 rounded-full bg-brand-soft flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-brand" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Signed in · saved to your account</p>
                <p className="text-sm font-semibold text-ink truncate">{[user.name, user.phone, user.email].filter(Boolean).join(" · ")}</p>
              </div>
              <span className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white">
                <Check className="w-3.5 h-3.5" />
              </span>
            </div>
          ) : (
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-brand/20 bg-brand-soft/50 px-4 py-3.5" data-testid="booking-signin-nudge">
              <p className="text-sm text-ink">Sign in for faster checkout and to keep this booking in your account.</p>
              <button onClick={() => setAuthOpen(true)} data-testid="booking-signin-btn" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-5 h-10 text-sm transition-transform hover:-translate-y-0.5">
                <LogIn className="w-4 h-4" /> Sign in
              </button>
            </div>
          )
        )}

        {loading && <DetailSkeleton />}

        {!loading && !room && (
          <div className="rounded-3xl border border-black/5 bg-white p-10 text-center" data-testid="room-not-found">
            <p className="text-ink font-medium">We couldn't find that room.</p>
            <Link to={`/stays/${propertyId}`} className="mt-4 inline-block rounded-full bg-ink text-white px-5 py-2.5 text-sm font-medium">View rooms</Link>
          </div>
        )}

        {room && (
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-10 items-start">
            {/* LEFT: gallery + details + inline dual-month calendar */}
            <div className="lg:col-span-3">
              <PhotoGallery media={media} alt={room.name} testid="room-gallery" />
              <div className="mt-6">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-heading font-extrabold text-3xl tracking-tight text-ink">{room.name}</h1>
                  <RatingBadge source={room} testid="room-rating-badge" />
                </div>
                {propQ.data?.name && <p className="mt-1 text-ink-soft">at {propQ.data.name}</p>}
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-soft">
                  <span className="inline-flex items-center gap-1.5" data-testid="room-max-people"><Users className="w-4 h-4 text-ink" /> Up to {maxAdults} adult{maxAdults !== 1 ? "s" : ""}{maxChildren > 0 ? ` + ${maxChildren} child${maxChildren !== 1 ? "ren" : ""}` : ""}</span>
                  {room.beds != null && <span className="inline-flex items-center gap-1.5"><BedDouble className="w-4 h-4 text-ink" /> {room.beds} bed{room.beds > 1 ? "s" : ""}</span>}
                  {room.type && <span className="capitalize">{room.type}</span>}
                </div>
                {room.description && <p className="mt-5 text-ink-soft leading-relaxed whitespace-pre-line">{room.description}</p>}
                {room.amenities?.length > 0 && (
                  <div className="mt-6">
                    <h2 className="font-heading font-bold text-lg text-ink mb-3">Amenities</h2>
                    <AmenityChips amenities={room.amenities} testid="room-detail-amenities" />
                  </div>
                )}
              </div>
              <PropertyRules rules={rules} testid="room-property-rules" />
              <InlineCalendar
                range={range} setRange={setRange} today={today} windowEnd={windowEnd}
                busyDates={busyDates} nights={nights}
                availLoading={availQ.isLoading} availError={availQ.isError}
              />
            </div>

            {/* RIGHT: floating sticky reservation card (desktop only) */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="rounded-2xl border border-black/10 bg-white shadow-[0_12px_48px_rgba(20,20,20,0.10)] p-6 sticky top-24" data-testid="booking-card">
                <div className="flex items-center justify-between gap-3">
                  <p><span className="font-heading font-extrabold text-[26px] text-ink" data-testid="room-price-incl-gst">{fmtAmount(priceInclGst)}</span><span className="text-ink-soft text-sm"> / night incl. GST</span></p>
                  <RatingBadge source={room} testid="card-rating-badge" />
                </div>

                <div className="mt-4">
                  <BookingFields
                    range={range} setRange={setRange} today={today} windowEnd={windowEnd}
                    busyDates={busyDates} availLoading={availQ.isLoading} availError={availQ.isError}
                    adults={adults} setAdults={setAdults} childrenCount={children} setChildrenCount={setChildren} maxAdults={maxAdults}
                    maxChildren={maxChildren} childRuleText={childRuleText} childrenAllowed={childrenAllowed}
                  />
                </div>

                <div className="mt-4"><GuestDetailsForm form={form} onChange={onChange} gst={gst} onGstChange={(g) => { setGst(g); setGstError(""); }} gstError={gstError} /></div>
                <div className="mt-4"><CouponField {...couponProps} /></div>

                {nights > 0 && (
                  <div className="mt-4 border-t border-black/5 pt-4">
                    <PriceBreakdown quote={quote} nights={nights} loading={quoteQ.isFetching} error={quoteError} />
                  </div>
                )}

                <button onClick={goConfirm} data-testid="book-pay-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold h-12 text-[15px] transition-transform hover:-translate-y-0.5">
                  Reserve
                </button>
                <p className="mt-2 text-center text-xs text-ink-faint">You won't be charged yet — review and pay securely on the next step.</p>

                <TrustBadges className="mt-4 pt-4 border-t border-black/5" />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* sticky mobile bottom bar → opens reserve bottom-sheet */}
      {room && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur border-t border-black/10 px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center justify-between gap-4" data-testid="mobile-book-bar">
          <button type="button" onClick={() => setSheetOpen(true)} className="text-left min-w-0" data-testid="mobile-bar-summary">
            {nights > 0 ? (
              <>
                <p className="font-heading font-bold text-ink" data-testid="mobile-bar-total">{total != null ? fmtAmount(total) : "Checking…"} <span className="text-xs font-sans font-normal text-ink-soft">total</span></p>
                <p className="text-xs text-ink-soft underline underline-offset-2">{format(range.from, "dd MMM")} – {format(range.to, "dd MMM")} · {nights} night{nights !== 1 ? "s" : ""}</p>
              </>
            ) : (
              <>
                <p className="font-heading font-bold text-ink">{fmtAmount(priceInclGst)} <span className="text-xs font-sans font-normal text-ink-soft">/ night incl. GST</span></p>
                <p className="text-xs text-ink-soft underline underline-offset-2">Add dates for total</p>
              </>
            )}
          </button>
          <button onClick={() => (nights > 0 ? setFlowOpen(true) : setSheetOpen(true))} data-testid="mobile-book-btn" className="shrink-0 inline-flex items-center justify-center rounded-full bg-brand text-white font-semibold h-12 px-7">
            {nights > 0 ? "Reserve" : "Select dates"}
          </button>
        </div>
      )}

      {room && (
        <ReserveSheet
          open={sheetOpen} onOpenChange={setSheetOpen}
          room={room} price={priceInclGst} nights={nights}
          range={range} setRange={setRange} today={today} windowEnd={windowEnd}
          busyDates={busyDates} availLoading={availQ.isLoading}
          adults={adults} setAdults={setAdults} childrenCount={children} setChildrenCount={setChildren} maxAdults={maxAdults}
          maxChildren={maxChildren} childRuleText={childRuleText} childrenAllowed={childrenAllowed}
          couponProps={couponProps} total={total}
          onContinue={() => { setSheetOpen(false); setFlowOpen(true); }}
        />
      )}

      {room && (
        <MobileCheckoutFlow
          open={flowOpen} onClose={() => setFlowOpen(false)}
          room={room} property={propQ.data}
          range={range} setRange={setRange} today={today} windowEnd={windowEnd} busyDates={busyDates} nights={nights}
          adults={adults} setAdults={setAdults} childrenCount={children} setChildrenCount={setChildren} maxAdults={maxAdults}
          maxChildren={maxChildren} childRuleText={childRuleText} childrenAllowed={childrenAllowed}
          rules={rules} quoteTerms={quoteTerms} quoteLoading={quoteQ.isFetching}
          quote={quote} quoteError={quoteError} total={total}
          form={form} onChange={onChange}
          gst={gst} onGstChange={(g) => { setGst(g); setGstError(""); }} gstError={gstError}
          user={user} onSubmit={submit}
        />
      )}

      <div className="hidden lg:block"><Footer /></div>

      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        title="Sign in for faster checkout"
        description="We'll autofill your details and save this booking to your account."
      />
    </div>
  );
};

export default RoomBooking;
